import {
  Icon,
  ImageFallback,
  InputStepper,
  TableBody,
  Text,
  Tooltip,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, ColorTypes, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Button } from 'native-base';
import React from 'react';

import { LateralBar } from '../../../../../../components/LateralBar';

import { EditProductItemProps } from './index';

const DesktopItem: React.FC<EditProductItemProps> = ({
  item,
  iconColor,
  amountItem,
  totalPrice,
  priceItem,
  handleChangeAmountItem,
  handleChangePriceItem,
  lateralBarColor,
  onViewOffers,
}) => {
  return (
    <TableBody testID="edit-equity-order-desktop" height={172}>
      <LateralBar
        barColor={lateralBarColor}
        borderBottomLeftRadius={BoxRadius.sm}
        borderTopLeftRadius="none"
      />
      <VStack w={'100%'} flex={1} px={Spacing['inline.xxs']} space={Spacing['inline.xxxs']}>
        <HStack alignItems={'center'} w={'100%'} justifyContent={'space-between'}>
          <HStack space={Spacing['inline.xxxs']} flex={1} maxW="180px">
            <ImageFallback
              src={item.image || ''}
              alt="logo"
              width="54px"
              height="48px"
              testID="product-img"
              resizeMode="cover"
              borderRadius={BoxRadius.sm}
              resizeMethod="scale"
            />
            <Tooltip testId="qa" label={item.productName || ''}>
              <VStack flex={1}>
                <Text
                  variant="semibold"
                  fontSize={Typography['fontsize.md']}
                  color={'neutral.darkest'}
                  noOfLines={1}
                  lineHeight="distant">
                  {item.productName}
                </Text>

                <Text
                  variant="regular"
                  fontSize={Typography['fontsize.md']}
                  color={'neutral.darkest'}
                  lineHeight="distant">
                  {item.symbol}
                </Text>
              </VStack>
            </Tooltip>
          </HStack>
          <VStack flex={1} maxW="180px">
            <Text
              variant="semibold"
              fontSize={Typography['fontsize.xs']}
              color={'neutral.black'}
              lineHeight="medium">
              Último preço
            </Text>
            <HStack space={Spacing['inline.nano']} alignItems={'center'}>
              <Text
                variant="regular"
                fontSize={Typography['fontsize.md']}
                color={'neutral.darkest'}
                lineHeight="distant">
                {item.lastPrice}
              </Text>
              <HStack alignItems={'center'} justifyContent={'center'}>
                {item.priceChangeTaxValue !== 0 && (
                  <Icon
                    name={item.priceChangeTaxValue >= 0 ? 'ArrowUp' : 'ArrowDown'}
                    size="xxxxxs"
                    color={Color[iconColor] as ColorTypes.Option}
                    testId="last-price-arrow"
                  />
                )}
                <Text
                  variant="regular"
                  fontSize={Typography['fontsize.md']}
                  color={
                    item.priceChangeTaxValue === 0
                      ? 'neutral.black'
                      : (iconColor as ColorTypes.Option)
                  }
                  lineHeight="distant">
                  {item.priceChangeTax}
                </Text>
              </HStack>
            </HStack>
            <Text
              variant="regular"
              fontSize={Typography['fontsize.xs']}
              color={'neutral.medium'}
              lineHeight="distant">
              {item.tradeDate}
            </Text>
          </VStack>
          <VStack flex={1} maxW="180px">
            <Text
              variant="semibold"
              fontSize={Typography['fontsize.xs']}
              color={'neutral.black'}
              lineHeight="medium">
              Melhor Compra
            </Text>
            <Tooltip testId="total-price" label={item.bidPrice || ''}>
              <VStack>
                <Text
                  variant="regular"
                  fontSize={Typography['fontsize.xs']}
                  color={'neutral.darkest'}
                  lineHeight="medium">
                  {item.bidPrice}
                </Text>
              </VStack>
            </Tooltip>
          </VStack>
          <VStack flex={1} maxW="180px">
            <Text
              variant="semibold"
              fontSize={Typography['fontsize.xs']}
              color={'neutral.black'}
              lineHeight="medium">
              Melhor Venda
            </Text>
            <Tooltip testId="total-price" label={item.askPrice || ''}>
              <VStack>
                <Text
                  variant="regular"
                  fontSize={Typography['fontsize.xs']}
                  color={'neutral.darkest'}
                  numberOfLines={1}
                  lineHeight="medium">
                  {item.askPrice}
                </Text>
              </VStack>
            </Tooltip>
          </VStack>
          <VStack flex={1} maxW="125px">
            <Text
              variant="semibold"
              fontSize={Typography['fontsize.xs']}
              color={'neutral.black'}
              lineHeight="medium">
              Fechamento
            </Text>
            <Tooltip testId="total-price" label={item.closePrice || ''}>
              <VStack>
                <Text
                  variant="regular"
                  fontSize={Typography['fontsize.xs']}
                  color={'neutral.darkest'}
                  numberOfLines={1}
                  lineHeight="medium">
                  {item.closePrice}
                </Text>
              </VStack>
            </Tooltip>
          </VStack>
        </HStack>
        <VStack space={Spacing['inline.nano']}>
          <HStack flex={1} alignItems={'center'} justifyContent={'space-between'}>
            <VStack flex={1} maxW="180px">
              <Text
                variant="semibold"
                fontSize={Typography['fontsize.xs']}
                color={'neutral.black'}
                lineHeight="medium">
                Tipo da ordem
              </Text>
              <Text
                variant="regular"
                fontSize={Typography['fontsize.xs']}
                color={'neutral.darkest'}
                numberOfLines={1}
                lineHeight="medium">
                Limitada
              </Text>
            </VStack>
            <VStack space={Spacing['inline.nano']} maxW="180px">
              <Text
                variant="semibold"
                fontSize={Typography['fontsize.xs']}
                color={'neutral.black'}
                lineHeight="medium">
                Quantidade
              </Text>
              <InputStepper
                stepperValue={item.lotSize || 1}
                max={1000000}
                min={item.lotSize || 1}
                name="equity-amount-order-input"
                testId="equity-amount-order-input"
                keyboardType="numeric"
                fontSize={Typography['fontsize.md']}
                defaultValue={amountItem}
                onChange={handleChangeAmountItem}
                showErrorMessage={false}
                {...((totalPrice === 0 || totalPrice > 1000000) && {
                  customError: 'Erro',
                })}
              />
            </VStack>
            <VStack space={Spacing['inline.nano']} maxW="180px">
              <Text
                variant="semibold"
                fontSize={Typography['fontsize.xs']}
                color={'neutral.black'}
                lineHeight="medium">
                Preço por ativo
              </Text>
              <InputStepper
                stepperValue={0.01}
                max={1000000}
                min={0.01}
                fontSize={Typography['fontsize.md']}
                keyboardType="numeric"
                ignoreStepperDivision
                maskOption="money"
                name="equity-amount-price"
                testId="equity-amount-price"
                defaultValue={priceItem || 0.01}
                onChange={handleChangePriceItem}
                showErrorMessage={false}
                {...((totalPrice === 0 || totalPrice > 1000000) && {
                  customError: 'Erro',
                })}
              />
            </VStack>
            <VStack flex={1} maxW="180px">
              <Text
                variant="semibold"
                fontSize={Typography['fontsize.md']}
                color={'neutral.black'}
                lineHeight="medium">
                Total estimado
              </Text>
              <Tooltip testId="total-price" label={Utils.Mask.Money(totalPrice) || ''}>
                <VStack>
                  <Text
                    variant="regular"
                    fontSize={Typography['fontsize.md']}
                    color={'neutral.darkest'}
                    lineHeight="distant">
                    {Utils.Mask.Money(totalPrice)}
                  </Text>
                </VStack>
              </Tooltip>
            </VStack>
            <HStack alignItems="center" flex={1} maxW="125px">
              <Button
                w="110px"
                testID="table-equity-offers"
                variant="primary"
                backgroundColor={Utils.ColorToken.WithOpacity('feedback.gen.lightest', 0.45)}
                _text={{ color: Color['brand.tertiary.principal'] }}
                shadow="none"
                borderRadius={BoxRadius.sm}
                onPress={onViewOffers}
                rightIcon={
                  <Icon name="Book" color={Color['brand.tertiary.principal']} size="xxs" />
                }>
                OFERTAS
              </Button>
            </HStack>
          </HStack>
          {(totalPrice === 0 || totalPrice > 1000000) && (
            <Text
              testId={'message-error'}
              variant="regular"
              fontSize={Typography['fontsize.xs']}
              lineHeight="distant"
              color="feedback.error.default">
              Este ativo atingiu um valor inválido, altere a quantidade ou preço
            </Text>
          )}
        </VStack>
      </VStack>
    </TableBody>
  );
};

export const Desktop = React.memo(DesktopItem);
